<template>
  <form-layout :loading="isLoading" @save="updateTaskOrWorkOrder">
    <template #title>
      <h1 class="uppercase">{{ titleText }}</h1>
    </template>
    <template #body v-if="formData">
      <div class="info">
        <p class="text-lg">{{ taskOrWorkOrder[nameProp] }}</p>
        <p class="mt-2" v-if="taskOrWorkOrder.hours_scoped">
          Estimated hours: {{ taskOrWorkOrder.hours_scoped }}
        </p>
        <template v-if="taskOrWorkOrder.contract">
          <p
            class="mt-2"
            v-if="
              taskOrWorkOrder.contract.primary_contact &&
                taskOrWorkOrder.contract.primary_contact.name
            "
          >
            {{ taskOrWorkOrder.contract.primary_contact.name }}
          </p>
          <template v-if="taskOrWorkOrder.contract.place">
            <p v-if="taskOrWorkOrder">
              {{ taskOrWorkOrder.contract.place.address_1 }}
              <template v-if="taskOrWorkOrder.contract.place.address_2">
                - {{ taskOrWorkOrder.contract.place.address_2 }}
              </template>
            </p>
            <p>
              {{ taskOrWorkOrder.contract.place.city }},
              {{ taskOrWorkOrder.contract.place.state }}
              {{ taskOrWorkOrder.contract.place.zip }}
            </p>
          </template>
        </template>
      </div>

      <bdg-radios-group
        ref="status"
        name="Status"
        :options="statusOptions"
        v-model="formData.status"
        required
      />

      <bdg-dropdown
        ref="user"
        name="Assigned to"
        :options="users"
        v-model="formData.user_id"
        searchable
        required
        v-if="userType === UserTypes.CREW_SUPERVISOR"
      />

      <template v-if="task">
        <div class="flex mt-4">
          <bdg-number-field
            ref="crewLeaderHours"
            name="Crew leader hours"
            v-model="formData.crew_leader_hours"
            force-int
            required
          />
          <bdg-number-field
            ref="crewMemberHours"
            name="Crew member hours"
            v-model="formData.crew_member_hours"
            force-int
            required
          />
        </div>

        <bdg-number-field
          ref="crewMembersPresent"
          class="w-1/2 pr-2"
          name="Crew members present"
          v-model="formData.crew_members_present"
          required
        />
      </template>

      <bdg-files-list
        class="mt-4"
        section-name="Attached files (optional)"
        :items="formData.files"
        @delete="removeFile"
      />
      <bdg-file-uploader
        :class="{
          'mt-2': formData.files && formData.files.length
        }"
        @change="onFileChange"
        multiple
      />

      <template v-if="workOrder">
        <bdg-text-area name="Notes" v-model="formData.note.text" />
      </template>
    </template>
  </form-layout>
</template>

<script>
import FormLayout from "../views/layouts/FormLayout";
import BdgRadiosGroup from "./fields/BdgRadiosGroup";
import { CompletionStatusLabels } from "../constants/enums";
import { validateRequiredRefs } from "../helpers";
import BdgFileUploader from "../elements/files-list/BdgFileUploader";
import BdgFilesList from "../elements/files-list/BdgFilesList";
import BdgTextArea from "./fields/BdgTextArea";
import { UserTypes } from "../constants/user-enums";
import BdgDropdown from "./fields/BdgDropdown";
import BdgNumberField from "./fields/BdgNumberField";

export default {
  name: "UpdateTaskForm",
  components: {
    BdgNumberField,
    BdgDropdown,
    BdgTextArea,
    BdgFilesList,
    BdgFileUploader,
    BdgRadiosGroup,
    FormLayout
  },
  props: {
    task: Object,
    workOrder: Object
  },
  data() {
    return {
      formData: null,
      statusOptions: Object.keys(CompletionStatusLabels).map(key => {
        return {
          label: CompletionStatusLabels[key],
          value: key
        };
      }),
      UserTypes
    };
  },
  computed: {
    userType() {
      return this.$store.state.userType;
    },
    titleText() {
      return this.task ? "Update Task" : "Update Work Order";
    },
    nameProp() {
      return this.task ? "name" : "title";
    },
    taskOrWorkOrder() {
      return this.task ? this.task : this.workOrder;
    },
    users() {
      const users = this.$store.state.Users.usersOfSpecifiedTypes;
      if (users) {
        return users.map(u => {
          return {
            label: u.name,
            value: u.id
          };
        });
      }
      return users;
    },
    requiredRefs() {
      if (this.task) {
        return [
          "status",
          "crewLeaderHours",
          "crewMemberHours",
          "crewMembersPresent"
        ];
      }
      return ["status"];
    },
    isUpdatingContractScopeItemTask() {
      return this.$store.state.Contracts.JobTracking
        .isUpdatingContractScopeItemTask;
    },
    isUpdatingWorkOrder() {
      return this.$store.state.Contracts.WorkOrders.isUpdatingWorkOrder;
    },
    isLoading() {
      return this.task
        ? this.isUpdatingContractScopeItemTask
        : this.isUpdatingWorkOrder;
    }
  },
  watch: {
    taskOrWorkOrder(newVal) {
      if (newVal) {
        this.formData = {
          id: newVal.id,
          user_id: newVal.user_id,
          contract_id: newVal.contract_id,
          status: newVal.status,
          crew_leader_hours: newVal.crew_leader_hours,
          crew_member_hours: newVal.crew_member_hours,
          crew_members_present: newVal.crew_members_present,
          files: newVal.files,
          note: newVal.note || { text: null }
        };
        if (this.task) {
          this.formData.work_scope_id = newVal.work_scope_id;
          this.formData.work_scope_item_id = newVal.work_scope_item_id;
        }
      }
    }
  },
  created() {
    this.formData = {
      id: this.taskOrWorkOrder.id,
      user_id: this.taskOrWorkOrder.user_id,
      contract_id: this.taskOrWorkOrder.contract_id,
      status: this.taskOrWorkOrder.status,
      crew_leader_hours: this.taskOrWorkOrder.crew_leader_hours,
      crew_member_hours: this.taskOrWorkOrder.crew_member_hours,
      crew_members_present: this.taskOrWorkOrder.crew_members_present,
      files: this.taskOrWorkOrder.files,
      note: this.taskOrWorkOrder.note || { text: null }
    };

    if (this.task) {
      this.formData.work_scope_id = this.taskOrWorkOrder.work_scope_id;
      this.formData.work_scope_item_id = this.taskOrWorkOrder.work_scope_item_id;
    } else if (this.workOrder) {
      this.formData.contact_id = this.taskOrWorkOrder.contact_id;
      this.formData.due_date = this.taskOrWorkOrder.due_date;
      this.formData.priority = this.taskOrWorkOrder.priority;
      this.formData.title = this.taskOrWorkOrder.title;
      this.formData.user_id = this.taskOrWorkOrder.user_id;
    }

    if (this.userType === UserTypes.CREW_SUPERVISOR) {
      this.$store.dispatch("getUsers", [
        UserTypes.CREW_SUPERVISOR,
        UserTypes.CREW_LEADER
      ]);
    }
  },
  methods: {
    onFileChange(files) {
      if (files && files.length) {
        this.formData.files = Array.from(files);
      }
    },
    removeFile(file) {
      this.formData.files.splice(this.formData.files.indexOf(file), 1);
    },
    validate() {
      return validateRequiredRefs(this, true);
    },
    updateTaskOrWorkOrder() {
      if (!this.validate()) {
        return;
      }

      if (this.task) {
        this.$store.dispatch("updateContractScopeItemTask", {
          ...this.formData,
          isDashboardView: true
        });
      } else if (this.workOrder) {
        this.$store.dispatch("updateWorkOrder", {
          ...this.formData,
          isDashboardView: true
        });
      }
    }
  }
};
</script>

<style scoped></style>
